//Gallery/MyLikes.tsx

import React, { useEffect, useState, useRef} from 'react';
import { useNavigate} from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import {PostSection} from "./GalleryUIMobileNR";
import type {MediaEntry} from "./GalleryUIMobileNR";
import { useTranslation } from 'react-i18next';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import { FaBars } from 'react-icons/fa';

interface Post {  
  _id: string;
  media: MediaEntry[];
  captions: string[];
  createdAt: string;

  likes: number;
  likedByUser?: boolean;

  likeKey?: string;
  totalLikes?: number;

  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };

  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;

  hashtags?: string[];
  status?: string;

  isAd?: boolean;
  adLabel?: string;
  adCtaText?: string;
  adUrl?: string;
  advertiserName?: string;
}

// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const IMAGE_BASE = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_BASE) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const toImageUrl = (p: string): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;

  const base = IMAGE_BASE.replace(/\/$/, '');
  const rel  = p.replace(/^\//, '');
  return `${base}/${rel}`;
};


const formatMediaArray = (raw: any[]): MediaEntry[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((m) => {
      // 🎬 HLS / new video pipeline
      if (m?.isHls === true || m?.videoId || m?.hlsUrl || m?.dashUrl) {
        return {
          isHls: true,
          videoId: m.videoId ?? null,
          hlsUrl: m.hlsUrl ?? null,
          dashUrl: m.dashUrl ?? null,
          posterUrl: m.posterUrl ?? m.poster ?? null,
          musicId: m.musicId ?? null,
          musicTitle: m.musicTitle ?? null,
          musicArtist: m.musicArtist ?? null,
        } as MediaEntry;
      }

      // 🖼 image
      const url = toImageUrl(m?.url || m?.path || m?.src || '');

      if (!url) return null;

      return {
        url,
        isHls: false,
        posterUrl: m?.posterUrl ?? null,
        musicId: m?.musicId ?? null,
        musicTitle: m?.musicTitle ?? null,
        musicArtist: m?.musicArtist ?? null,
      } as MediaEntry;
    })
    .filter(Boolean) as MediaEntry[];
};

// Limiting Memory
const WINDOW_BEFORE = 1;
const WINDOW_AFTER = 2;
const MAX_POSTS_IN_MEMORY = 60;
const LIMIT = 20;

const MyLikes = () => {
  const [likedPosts, setLikedPosts] = useState<Post[]>([]);
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();

  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [globalMuted, setGlobalMuted] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const isFetchingRef = React.useRef(false);
  const offsetRef = React.useRef(0);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { unlikePost, refreshLikes, likedIds } = useLikes();


  const hasMoreRef = useRef(true);
  const didFetchOnceRef = useRef(false);


  //Loading
  const isInitialLoading = isFetching && likedPosts.length === 0;

  // --- music hover state (per media tile) ---
  const hoverTimeoutRef = useRef<number | null>(null);



  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  
  const handleOpenMusic = async (entry: MediaEntry) => {
    // 1️⃣ If we already know the track id, go straight to details
    if (entry.musicId) {
      navigate(`/musicdetails/${entry.musicId}`);
      return;
    }
  
    // 2️⃣ Fallback: search by either title OR artist (not "title artist")
    const q = (entry.musicTitle || entry.musicArtist || '').trim();
    if (!q) {
      console.warn('[MyLikes.handleOpenMusic] no title/artist on entry', entry);
      return;
    }
  
    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
      if (!list.length) {
        console.warn('[MyLikes.handleOpenMusic] no music match for', q);
        return;
      }
  
      navigate(`/musicdetails/${list[0]._id}`);
    } catch (e) {
      console.error('[MyLikes.handleOpenMusic] music lookup failed:', e);
    }
  };


  // ---- SEARCH HELPERS for My Likes (hashtags, captions, location, etc.) ----
  const normText = (s?: string | null) => (s || '').toLowerCase();

  const matchesSearch = (post: Post, rawQuery: string): boolean => {
    const q = rawQuery.trim();
    if (!q) return true; // empty search → no filtering

    // remove leading '#' so both "#budapest" and "budapest" work
    const needle = q.replace(/^#/, '').toLowerCase();

    const haystack: string[] = [];
    const anyPost: any = post;

    // Text fields if present
    if (anyPost.caption) haystack.push(anyPost.caption);
    if (anyPost.description) haystack.push(anyPost.description);
    if (anyPost.title) haystack.push(anyPost.title);

    // Hashtags / tags arrays
    if (Array.isArray(anyPost.hashtags)) haystack.push(...anyPost.hashtags);
    if (Array.isArray(anyPost.tags)) haystack.push(...anyPost.tags);

    // Location fields
    if (post.location?.city) haystack.push(post.location.city);
    if (post.location?.country) haystack.push(post.location.country);

    // Also allow searching by author's profilename
    if (post.user?.profilename) haystack.push(post.user.profilename);

    // Substring, case-insensitive
    return haystack.some((txt) => txt && normText(txt).includes(needle));
  };

  const filteredLikedPosts = React.useMemo(
    () => likedPosts.filter((p) => matchesSearch(p, searchQuery)),
    [likedPosts, searchQuery]
  );

  
  // Fetch liked posts
  const fetchLikedPosts = async () => {
if (!hasMoreRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);

    try {
      const start = offsetRef.current;
      const res = await api.get('/likes/liked/mine', {
        params: { offset: start, limit: LIMIT },
      });

      const newPostsRaw: any[] = res.data || [];

const newPosts: Post[] = newPostsRaw.map((p: any) => {
  const media = formatMediaArray(p.media);
  const likeKey = likeKeyFromRow(p);

  const likesCount =
    typeof p.likes === 'number'
      ? p.likes
      : typeof p.totalLikes === 'number'
      ? p.totalLikes
      : 0;

  return {
    // ✅ real post id (NOT likeKey)
    _id: String(p._id),

    // ✅ canonical like id
    likeKey,

    // ✅ media normalized
    media,

    // ✅ captions normalized
    captions: Array.isArray(p.captions)
      ? p.captions
      : p.caption
      ? [p.caption]
      : [],

    // ✅ created date fallback
    createdAt: p.createdAt || new Date().toISOString(),

    // ✅ likes synced
    likes: likesCount,
    totalLikes: likesCount,
    likedByUser: true, // MyLikes always liked

    // ✅ user block (required by PostSection header)
    user: p.user
      ? {
          _id: String(p.user._id),
          profilename: p.user.profilename || 'User',
          picture: p.user.picture ?? null,
        }
      : null,

    // ✅ location normalized (PostSection safe)
    location: p.location
      ? {
          city: p.location.city || '',
          country: p.location.country || '',
          countryCode: p.location.countryCode ?? undefined,
        }
      : undefined,

    // ✅ optional meta
    hashtags: Array.isArray(p.hashtags) ? p.hashtags : [],
    status: p.status ?? undefined,

    // ✅ ad support (safe defaults)
    isAd: !!p.isAd,
    adLabel: p.adLabel ?? undefined,
    adCtaText: p.adCtaText ?? undefined,
    adUrl: p.adUrl ?? undefined,
    advertiserName: p.advertiserName ?? undefined,
  };
});

setLikedPosts(prev => {
  const seen = new Set(prev.map(p => p._id));
  const merged = [...prev];
  for (const p of newPosts) {
    if (!seen.has(p._id)) merged.push(p);
  }
  return merged.slice(-MAX_POSTS_IN_MEMORY);
});

if (newPosts.length < LIMIT) {
  setHasMore(false);
  hasMoreRef.current = false;
}

      offsetRef.current = start + newPosts.length;
    } catch (err) {
      console.error('❌ Failed to fetch liked posts:', err);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      didFetchOnceRef.current = true;
    }
  };

  useEffect(() => {
    refreshLikes();
  }, [refreshLikes]);

  useEffect(() => {
  if (!token) return;

  setLikedPosts([]);
  setHasMore(true);
  hasMoreRef.current = true;

  offsetRef.current = 0;
  isFetchingRef.current = false;
  setIsFetching(false);

  fetchLikedPosts();
}, [token]);

  // Only one Video at a time
  useEffect(() => {
    const onPlay = (e: Event) => {
      const el = e.target as HTMLVideoElement | null;
      if (!el || el.tagName !== 'VIDEO') return;

      document.querySelectorAll('video').forEach((v) => {
        if (v !== el) {
          try { v.pause(); } catch {}
        }
      });
    };

    document.addEventListener('play', onPlay, true);
    return () => document.removeEventListener('play', onPlay, true);
  }, []);

  //Loading effect
  useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);
  return () => style.remove();
}, []);


  // Unlike handler
  const removeLike = async (post: Post) => {
    const prev = likedPosts;
setLikedPosts(p => p.filter(x => x.likeKey !== post.likeKey));

    try {
await unlikePost(post.likeKey || post._id);
    } catch (err) {
      console.error('❌ Failed to remove like:', err);
      setLikedPosts(prev); // rollback on error
    }
  };

  
useEffect(() => {
  if (!didFetchOnceRef.current) return;
  setLikedPosts(prev => prev.filter(p => likedIds.has(p.likeKey || p._id)));
}, [likedIds]);

const lastSize = React.useRef(likedIds.size);


  useEffect(() => {
  if (likedIds.size > lastSize.current) {
    setLikedPosts([]);
    setHasMore(true);
    hasMoreRef.current = true;

    offsetRef.current = 0;
    fetchLikedPosts();
  }
  lastSize.current = likedIds.size;
}, [likedIds]);


return (
  <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

    {/* FEED */}
    <div
  ref={feedRef}
  onScroll={(e) => {
    const el = e.currentTarget;
    const idx = Math.round((el.scrollTop || 0) / (el.clientHeight || 1));
    if (idx !== pageIndex) setPageIndex(idx);

    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 300;

    if (nearBottom && hasMoreRef.current && !isFetchingRef.current) {
      fetchLikedPosts();
    }
  }}
      style={{
        flexGrow: 1,
        overflowY: 'auto',
        height: '100dvh',
        backgroundColor: '#000',
        scrollSnapType: 'y mandatory',
        touchAction: 'pan-y',
        overscrollBehaviorY: 'contain',
      }}
    >
     {isInitialLoading ? (
  <div
    style={{
      height: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 14,
      color: '#bbb',
      fontSize: 16,
      letterSpacing: 0.4,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />

   <div>{t('MyLikes.loading')}</div>
  </div>
) : filteredLikedPosts.length > 0 ? (

filteredLikedPosts.map((post, idx) => {
  const isActive = idx === pageIndex;
  const inWindow = idx >= pageIndex - 1 && idx <= pageIndex + 1;

  const shouldRender =
    idx >= pageIndex - WINDOW_BEFORE &&
    idx <= pageIndex + WINDOW_AFTER;

  if (!shouldRender) {
    return (
      <section
        key={post._id}
        data-post-id={post._id}
        style={{
          height: '100dvh',
          minHeight: '100dvh',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          margin: 0,
          padding: 0,
        }}
      />
    );
  }

  return (
    <section
      key={post._id}
      data-post-id={post._id}
      style={{
        height: '100dvh',
        minHeight: '100dvh',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        margin: 0,
        padding: 0,
      }}
    >
      <PostSection
        post={post}
        isActive={isActive}
        inWindow={inWindow}
        globalMuted={globalMuted}
        navigate={navigate}
        onLike={() => {}}
        onUnlike={() => removeLike(post)}
        currentUserId={user?._id}
        onMusicClick={handleOpenMusic}
      />
    </section>
  );
})
) : (
  <p style={{ color: '#aaa', padding: 16 }}>
    {t('myLikes.noPosts') || 'No liked posts yet'} 🤍
  </p>
)}
    </div>

    {/* MOBILE MENU BUTTON */}
    <div
      onClick={() => navigate('/menu')}
      style={{
        position: 'absolute',
        top: 14,
        right: 17,
        padding: 10,
        cursor: 'pointer',
        zIndex: 10,
      }}
    >
      <FaBars style={{ color: '#bbb', fontSize: 19 }} />
    </div>
  </div>
);
};

export default MyLikes;